import type { Invoice, Tariff } from './types';
import { INVOICE_PARSE_PROMPT, TARIFF_PARSE_PROMPT, createInvoiceResponseFormat, createTariffResponseFormat } from './prompts';
import { useState, useCallback } from 'react';

// En desarrollo usa Wrangler en puerto 8788, en producción usa el path relativo
const API_BASE = import.meta.env.DEV 
  ? 'http://localhost:8788/api'
  : '/api';

interface OpenAIResponse {
  choices: Array<{
    message: {
      content: string;
    };
  }>;
}

/**
 * Cliente para comunicarse con el proxy de OpenAI en Cloudflare Pages Functions
 */
class OpenAIClient {
  private async callInvoiceEndpoint(invoiceText: string): Promise<any> {
    try {
      console.log('🌐 Calling parse-invoice endpoint...');
      const response = await fetch(`${API_BASE}/parse-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ invoiceText }),
      });

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(`API Error ${response.status}: ${errorData.error || 'Unknown error'}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Parse Invoice Error:', error);
      throw error;
    }
  }

  private async callTariffEndpoint(tariffText: string): Promise<any> {
    try {
      console.log('🌐 Calling parse-tariff endpoint...');
      const response = await fetch(`${API_BASE}/parse-tariff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tariffText }),
      });

      if (!response.ok) {
        const errorData = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(`API Error ${response.status}: ${errorData.error || 'Unknown error'}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Parse Tariff Error:', error);
      throw error;
    }
  }

  /**
   * Parsea el contenido de una factura PDF (texto extraído) usando OpenAI
   */
  async parseInvoice(invoiceText: string): Promise<Invoice> {
    console.log('🔄 Parsing invoice with secure endpoint...');
    
    try {
      console.log('📡 Calling secure parse-invoice endpoint...');
      const response: OpenAIResponse = await this.callInvoiceEndpoint(invoiceText);
      
      if (!response.choices?.[0]?.message?.content) {
        throw new Error('Invalid response format from OpenAI');
      }

      const parsedData = JSON.parse(response.choices[0].message.content);
      console.log('✅ Invoice parsed successfully via secure endpoint');
      console.log('📊 Extracted data:', parsedData);
      return parsedData as Invoice;
      
    } catch (error) {
      console.error('❌ Secure invoice parsing failed:', error);
      
      // Fallback simple solo para desarrollo
      if (import.meta.env.DEV) {
        console.log('🧪 Using development fallback - Configure valid OPENAI_API_KEY in .dev.vars');
        await new Promise(resolve => setTimeout(resolve, 500));
        return createMockInvoiceData();
      }
      
      throw error;
    }
  }

  /**
   * Parsea el texto de una tarifa comercial usando OpenAI
   */
  async parseTariff(tariffText: string): Promise<Tariff> {
    console.log('🔄 Parsing tariff with secure endpoint...');
    
    try {
      console.log('📡 Calling secure parse-tariff endpoint...');
      const response: OpenAIResponse = await this.callTariffEndpoint(tariffText);
      
      if (!response.choices?.[0]?.message?.content) {
        throw new Error('Invalid response format from OpenAI');
      }

      const rawContent = response.choices[0].message.content;
      console.log('📄 Raw OpenAI response content:', rawContent);
      
      const parsedData = JSON.parse(rawContent);
      console.log('✅ Tariff parsed successfully via secure endpoint');
      console.log('📊 Extracted data:', parsedData);
      console.log('🔍 Type check - has required fields:', {
        name: !!parsedData.name,
        power: !!parsedData.power,
        energy: !!parsedData.energy,
        prices_include_vat: parsedData.prices_include_vat !== undefined
      });
      
      return parsedData as Tariff;
      
    } catch (error) {
      console.error('❌ Secure tariff parsing failed:', error);
      
      // Fallback simple solo para desarrollo
      if (import.meta.env.DEV) {
        console.log('🧪 Using development fallback - Configure valid OPENAI_API_KEY in .dev.vars');
        await new Promise(resolve => setTimeout(resolve, 300));
        return createMockTariffData();
      }
      
      throw error;
    }
  }
}

// Singleton instance
export const openaiClient = new OpenAIClient();

/**
 * Hook personalizado para manejar estados de carga y errores
 */
export function useAsyncOperation<T>() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(async (operation: () => Promise<T>): Promise<T | null> => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Starting async operation...');
      const result = await operation();
      console.log('✅ Async operation completed successfully:', result);
      return result;
    } catch (err) {
      console.error('❌ Async operation failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
      console.log('🏁 Async operation finished (loading set to false)');
    }
  }, []);

  return { loading, error, execute };
}



/**
 * Extrae texto de un archivo PDF usando PDF.js
 */
/**
 * Procesa un PDF completo: extrae texto y lo parsea con OpenAI directamente
 */
export async function processPDFInvoice(file: File): Promise<Invoice> {
  console.log('🔄 Processing PDF invoice:', file.name);
  
  try {
    // 1. Extraer texto del PDF
    const text = await extractTextFromPDF(file);
    
    // 2. Enviar directamente a OpenAI para parsing
    console.log('📤 Sending extracted text to OpenAI for parsing...');
    const invoice = await openaiClient.parseInvoice(text);
    
    return invoice;
  } catch (error) {
    console.error('❌ PDF processing failed:', error);
    throw new Error(`No se pudo procesar el PDF: ${error instanceof Error ? error.message : String(error)}`);
  }
}

/**
 * Extrae texto de un archivo PDF usando PDF.js (función interna)
 */
async function extractTextFromPDF(file: File): Promise<string> {
  try {
    // Importar PDF.js dinámicamente
    const pdfjsLib = await import('pdfjs-dist');
    
    // Configurar worker - usar versión local desde node_modules
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url
    ).toString();
    
    // Leer archivo como ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    
    // Cargar PDF
    console.log('📄 Loading PDF with', arrayBuffer.byteLength, 'bytes');
    const pdf = await pdfjsLib.getDocument(arrayBuffer).promise;
    
    let fullText = '';
    
    console.log('✅ PDF loaded successfully, pages:', pdf.numPages);
    
    // Extraer texto de todas las páginas
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      console.log(`📃 Processing page ${pageNum}/${pdf.numPages}`);
      
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      
      console.log(`   Found ${textContent.items.length} text items`);
      
      // Concatenar todo el texto de la página
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      
      console.log(`   Page text length: ${pageText.length} characters`);
      
      fullText += pageText + '\n\n';
    }
    
    if (!fullText.trim()) {
      throw new Error('No se pudo extraer texto del PDF');
    }
    
    console.log('🎉 PDF text extraction completed!');
    console.log('Total text length:', fullText.length, 'characters');
    console.log('Preview:', fullText.substring(0, 200) + '...');
    
    return fullText.trim();
    
  } catch (error) {
    console.error('Error extracting PDF text:', error);
    
    // Fallback: mostrar mensaje de error más útil
    const fileName = file.name;
    return `Error al procesar PDF: ${fileName}

El archivo PDF no se pudo procesar automáticamente. 
Por favor, copia y pega manualmente el texto de tu factura:

INFORMACIÓN NECESARIA:
- Período de facturación (fechas de inicio y fin)
- Potencia contratada P1 y P2 (kW y €/kW·día) 
- Consumos P1, P2, P3 (kWh y €/kWh)
- Alquiler de equipos, descuentos/bonos
- Impuesto eléctrico, IVA, Total factura

Pega toda la información disponible y el sistema extraerá automáticamente los datos relevantes.`;
  }
}

/**
 * Mock data simple para desarrollo cuando OpenAI no está configurado
 */

const createMockInvoiceData = (): Invoice => ({
  period: {
    start: "2025-07-13",
    end: "2025-08-12", 
    days: 31
  },
  power: {
    p1_kw: 7.4,
    p2_kw: 7.4,
    p3_kw: 7.4,
    p1_eur_kw_day: 0.127406,
    p2_eur_kw_day: 0.080000,
    p3_eur_kw_day: 0.049264
  },
  energy: {
    p1_kwh: 129,
    p2_kwh: 181,
    p3_kwh: 553,
    p1_eur_kwh: 0.1136,
    p2_eur_kwh: 0.1136,
    p3_eur_kwh: 0.1136
  },
  extras: {
    equipment: 0.83,
    social_bonus: 0.40
  },
  taxes: {
    electricity_tax: 7.10,
    vat: 30.85
  },
  total_pdf: 177.74
});

/**
 * Mock data simple para desarrollo cuando OpenAI no está disponible
 */
const createMockTariffData = (): Tariff => ({
  name: "Tarifa Mock - Configura OPENAI_API_KEY en .dev.vars",
  power: {
    p1_eur_kw_day: 0.120,
    p2_eur_kw_day: 0.080,
    p3_eur_kw_day: 0.044
  },
  energy: {
    p1_eur_kwh: 0.160,
    p2_eur_kwh: 0.140,
    p3_eur_kwh: 0.110
  },
  prices_include_vat: true
});
